import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Container,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import axios from "axios";

function MobileFolderGroups() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editedName, setEditedName] = useState("");
  const [summary, setSummary] = useState({
    products: 0,
    items: 0,
    totalValue: 0,
    drafts: 0,
  });
  const navigate = useNavigate();

  // --- search state (overlay canvas) ---
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");

  // --- API calls ---
  const runGlobalSearch = async (q) => {
    const query = (q || "").trim();
    if (query.length < 2) {
      setSearchResults([]);
      setSearchError("");
      return;
    }
    try {
      setSearchLoading(true);
      setSearchError("");
      const res = await axios.get(
        "https://server-9p4hx.kinsta.app/api/stocmanager/products-search",
        {
          params: { q: query, limit: 50 },
        }
      );
      setSearchResults(res.data?.results || []);
    } catch (err) {
      console.error("Eroare la /products/search:", err);
      setSearchError(err.response?.data?.error || "Eroare la căutare.");
    } finally {
      setSearchLoading(false);
    }
  };

  const fetchLatestImageForGroup = async (groupId) => {
    try {
      const res = await axios.get(
        `https://server-9p4hx.kinsta.app/api/stocmanager/folder-groups/${groupId}/latest-image`
      );
      return res.data.latestImage;
    } catch (err) {
      console.error(
        `Eroare la preluarea imaginii pentru grupul ${groupId}:`,
        err
      );
      return null;
    }
  };

  const fetchGroupTotals = async (groupId) => {
    try {
      const res = await axios.get(
        `https://server-9p4hx.kinsta.app/api/stocmanager/folder-groups/${groupId}/summary`
      );
      return {
        totalProducts: res.data.products,
        totalValue: res.data.totalValue,
        itemsCount: res.data.items,
      };
    } catch (err) {
      console.error(`Eroare la summary pentru folder ${groupId}`, err);
      return { totalProducts: 0, totalValue: 0, itemsCount: 0 };
    }
  };

  const fetchGroups = async () => {
    try {
      const res = await axios.get(
        "https://server-9p4hx.kinsta.app/api/stocmanager/folder-groups"
      );
      const updated = await Promise.all(
        res.data.map(async (group) => {
          const [latestImage, totals] = await Promise.all([
            fetchLatestImageForGroup(group.id),
            fetchGroupTotals(group.id),
          ]);
          return {
            ...group,
            image_url: latestImage,
            totalProducts: totals.totalProducts,
            totalValue: totals.totalValue,
            itemsCount: totals.itemsCount,
          };
        })
      );
      setGroups(updated);
    } catch (err) {
      console.error("Eroare la încărcarea grupurilor:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupSummary = async () => {
    try {
      const res = await axios.get(
        "https://server-9p4hx.kinsta.app/api/stocmanager/folder-groups/summary"
      );
      setSummary({
        products: res.data.products,
        items: res.data.items,
        totalValue: res.data.totalValue,
        drafts: res.data.drafts || 0,
      });
    } catch (err) {
      console.error("Eroare la preluarea summary-ului:", err);
    }
  };

  useEffect(() => {
    fetchGroups();
    fetchGroupSummary();
  }, []);

  if (loading) {
    return (
      <Container sx={{ pt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container
      disableGutters
      sx={{
        p: 0,
        pt: 2,
        width: "100vw",
        maxWidth: "100vw",
        overflowX: "hidden",
        color: "#fff",
      }}
    >
      <Box
        sx={{
          pl: 2,
          pr: 1.5,
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold">
          Categorii Gestiuni
        </Typography>
        <IconButton
          size="small"
          onClick={(e) => {
            setAnchorEl(e.currentTarget);
            setSelectedGroup({ id: "__HEADER__" });
          }}
          sx={{ color: "#fff" }}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl) && selectedGroup?.id === "__HEADER__"}
          onClose={() => setAnchorEl(null)}
        >
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              navigate("/marketing");
            }}
          >
            <Typography variant="body2">Marketing</Typography>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              navigate("/scan");
            }}
          >
            <Typography variant="body2">Scanează</Typography>
          </MenuItem>
        </Menu>
      </Box>

      {/* Bara de căutare care doar deschide overlay-ul */}
      <Box sx={{ px: 2, mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Caută în toată baza de date (titlu sau SKU)"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setSearchOpen(true)}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "rgba(0,0,0,0.35)",
              color: "#fff",
              borderRadius: 2,
              "& fieldset": { borderColor: "rgba(255,255,255,0.35)" },
              "&:hover fieldset": { borderColor: "rgba(255,255,255,0.6)" },
              "&.Mui-focused fieldset": { borderColor: "#fff" },
            },
            "& .MuiInputBase-input::placeholder": {
              color: "rgba(255,255,255,0.75)",
              opacity: 1,
            },
          }}
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ mr: 1, color: "#fff" }} />
            ),
          }}
        />
      </Box>

      {/* OVERLAY DE CĂUTARE (Canvas alb full-screen) */}
      {searchOpen && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "#fff",
            color: "#111",
            zIndex: (theme) => theme.zIndex.modal + 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header cu X de închidere */}
          <Box
            sx={{
              p: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #eee",
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" sx={{ px: 1 }}>
              Căutare produse (global)
            </Typography>
            <IconButton
              onClick={() => {
                setSearchOpen(false);
                setSearchResults([]);
                setSearchError("");
                setSearchLoading(false);
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Bara de căutare în overlay, cu lupa pentru submit */}
          <Box sx={{ p: 2, borderBottom: "1px solid #eee" }}>
            <TextField
              fullWidth
              autoFocus
              size="small"
              placeholder="Caută după titlu sau SKU"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  runGlobalSearch(searchTerm);
                }
              }}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                ),
                endAdornment: (
                  <IconButton
                    aria-label="Caută"
                    onClick={() => runGlobalSearch(searchTerm)}
                    disabled={searchLoading || !searchTerm?.trim()}
                  >
                    <SearchIcon />
                  </IconButton>
                ),
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, display: "block" }}
            >
              Tastează cel puțin 2 caractere, apoi apasă pe lupă (sau Enter)
              pentru a căuta.
            </Typography>
          </Box>

          {/* Conținut rezultate */}
          <Box sx={{ p: 2, flex: 1, overflowY: "auto" }}>
            {searchLoading && (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} />
                <Typography variant="body2">
                  Caut în toată baza de date…
                </Typography>
              </Box>
            )}

            {!searchLoading && searchError && (
              <Alert severity="error">{searchError}</Alert>
            )}

            {!searchLoading &&
              !searchError &&
              (searchResults?.length ?? 0) === 0 && (
                <Typography variant="body2" color="text.secondary">
                  {searchTerm?.trim()?.length < 2
                    ? "Introdu cel puțin 2 caractere și apasă pe lupă."
                    : "Nu am găsit rezultate."}
                </Typography>
              )}

            {!searchLoading &&
              !searchError &&
              (searchResults?.length ?? 0) > 0 && (
                <Box>
                  {searchResults.map((p) => (
                    <Paper key={p.id} sx={{ p: 1.5, mb: 1 }}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={2}
                      >
                        {/* Product image on the left */}
                        {p.image_url ? (
                          <Box
                            component="img"
                            src={p.image_url}
                            alt={p.product_name || ""}
                            sx={{
                              width: 50,
                              height: 50,
                              objectFit: "cover",
                              borderRadius: 1,
                              mr: 2,
                              flexShrink: 0,
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 50,
                              height: 50,
                              backgroundColor: "#e0e0e0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: 1,
                              mr: 2,
                              flexShrink: 0,
                            }}
                          >
                            <PhotoCameraIcon
                              sx={{ fontSize: 24, color: "#9e9e9e" }}
                            />
                          </Box>
                        )}
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography fontWeight={600} noWrap>
                            {p.product_name || "(fără titlu)"}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            noWrap
                          >
                            SKU: {p.sku || "-"} • Folder: {p.folder_name || "-"}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2" color="text.secondary">
                            {Number(p.price || 0).toLocaleString()} RON
                          </Typography>
                          <Button
                            variant="contained"
                            size="small"
                            sx={{
                              backgroundColor: "#000",
                              color: "#fff",
                              "&:hover": { backgroundColor: "#333" },
                            }}
                            onClick={() => {
                              setSearchOpen(false);
                              navigate(`/mobile-product/${p.id}`);
                            }}
                          >
                            Deschide
                          </Button>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}
          </Box>
        </Box>
      )}

      {/* Summary */}
      <Paper
        sx={{
          p: 1.5,
          mb: 2,
          mx: 2,
          bgcolor: "rgba(0,0,0,0.35)",
          color: "#fff",
          border: "1px solid rgba(255,255,255,0.12)",
          backdropFilter: "blur(6px)",
        }}
      >
        <Box display="flex" justifyContent="space-between">
          <Typography fontSize={11} sx={{ color: "rgba(255,255,255,0.8)" }}>
            Bucăți
          </Typography>
          <Typography fontSize={11} sx={{ color: "rgba(255,255,255,0.8)" }}>
            Produse
          </Typography>
          <Typography
            fontSize={11}
            sx={{ color: "rgba(255,255,255,0.8)", mr: 7 }}
          >
            Ciorne nefăcute
          </Typography>
          <Typography fontSize={11} sx={{ color: "rgba(255,255,255,0.8)" }}>
            Valoare
          </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Typography fontWeight="bold" fontSize={14}>
            {summary.items || 0}
          </Typography>
          <Typography fontWeight="bold" fontSize={14}>
            {summary.products}
          </Typography>
          <Typography fontWeight="bold" fontSize={14}>
            {summary.drafts || 0}
          </Typography>
          <Typography fontWeight="bold" fontSize={14}>
            {summary.totalValue.toLocaleString()} RON
          </Typography>
        </Box>
      </Paper>

      {/* Lista grupurilor (Layer 1) */}
      {groups
        .filter((group) => Number(group.parent_id) === 0)
        .map((group) => (
          <Paper
            key={group.id}
            sx={{
              display: "flex",
              alignItems: "center",
              p: 2,
              mb: 2,
              cursor: "pointer",
              bgcolor: "rgba(0,0,0,0.35)",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.12)",
              backdropFilter: "blur(6px)",
              transition: "transform 0.15s ease, border-color 0.15s ease",
              "&:hover": {
                transform: "translateY(-1px)",
                borderColor: "rgba(255,255,255,0.3)",
              },
            }}
            onClick={() => {
              if (editingId !== group.id) {
                navigate(`/layer2/${group.id}`);
              }
            }}
          >
            {group.image_url ? (
              <Box
                component="img"
                src={group.image_url}
                sx={{
                  width: 50,
                  height: 50,
                  objectFit: "cover",
                  borderRadius: 1,
                  mr: 2,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  backgroundColor: "#e0e0e0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 1,
                  mr: 2,
                }}
              >
                <PhotoCameraIcon sx={{ fontSize: 24, color: "#9e9e9e" }} />
              </Box>
            )}
            <Box sx={{ flexGrow: 1 }}>
              {editingId === group.id ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <TextField
                    variant="standard"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    sx={{
                      flexGrow: 1,
                      "& .MuiInputBase-input": { color: "#fff" },
                      "& .MuiInput-underline:before": {
                        borderBottomColor: "rgba(255,255,255,0.4)",
                      },
                      "& .MuiInput-underline:hover:before": {
                        borderBottomColor: "#fff",
                      },
                      "& .MuiInput-underline:after": {
                        borderBottomColor: "#fff",
                      },
                    }}
                  />
                  <IconButton
                    onClick={async () => {
                      await axios.put(
                        `https://server-9p4hx.kinsta.app/api/stocmanager/folders/${group.id}`,
                        {
                          name: editedName,
                        }
                      );
                      setEditingId(null);
                      const res = await axios.get(
                        "https://server-9p4hx.kinsta.app/api/stocmanager/folder-groups"
                      );
                      setGroups(res.data);
                    }}
                    sx={{ color: "#fff" }}
                  >
                    <CheckIcon />
                  </IconButton>
                </Box>
              ) : (
                <>
                  <Typography fontWeight="bold">{group.name}</Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "rgba(255,255,255,0.8)" }}
                  >
                    {group.totalQuantity || 0} bucăți • {group.totalProducts}{" "}
                    produse • {Math.round(group.totalValue).toLocaleString()}{" "}
                    RON
                  </Typography>
                </>
              )}
            </Box>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
              setAnchorEl(e.currentTarget);
              setSelectedGroup(group);
            }}
              sx={{ ml: "auto", color: "#fff" }}
            >
              <MoreVertIcon />
            </IconButton>
          </Paper>
        ))}

      {/* Menu & Dialogs */}
      <Menu
        anchorEl={anchorEl}
        open={
          Boolean(anchorEl) &&
          selectedGroup &&
          selectedGroup.id !== "__HEADER__"
        }
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            setEditingId(selectedGroup.id);
            setEditedName(selectedGroup.name);
          }}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Editare
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            setConfirmDeleteOpen(true);
          }}
          sx={{ color: "error.main" }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Ștergere
        </MenuItem>
      </Menu>

      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
      >
        <DialogTitle>Confirmare ștergere</DialogTitle>
        <DialogContent>
          <Typography>
            Sigur dorești să ștergi folderul{" "}
            <strong>{selectedGroup?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Anulează</Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              try {
                await axios.delete(
                  `https://server-9p4hx.kinsta.app/api/stocmanager/folders/${selectedGroup.id}`
                );
                const res = await axios.get(
                  "https://server-9p4hx.kinsta.app/api/stocmanager/folder-groups"
                );
                setGroups(res.data);
                setSnackbarMsg("Folderul a fost șters.");
              } catch (err) {
                setSnackbarMsg(
                  err.response?.data?.error || "Eroare la ștergere."
                );
              } finally {
                setConfirmDeleteOpen(false);
                setSnackbarOpen(true);
              }
            }}
          >
            Șterge
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="warning"
          sx={{ width: "100%" }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>

      <IconButton
        onClick={() => setCreateOpen(true)}
        sx={{
          position: "fixed",
          bottom: 80,
          right: 20,
          width: 56,
          height: 56,
          borderRadius: "50%",
          backgroundColor: "#1976d2",
          color: "#fff",
          boxShadow: 2,
          "&:hover": {
            backgroundColor: "#1565c0",
          },
        }}
      >
        <AddIcon />
      </IconButton>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth>
        <DialogTitle>Adaugă Gestiune Principală</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nume Folder"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Anulează</Button>
          <Button
            variant="contained"
            onClick={async () => {
              setLoading(true);
              console.log(
                "[📤 FRONTEND] Trimit creare folder principal:",
                newFolderName
              );
              await axios.post(
                "https://server-9p4hx.kinsta.app/api/stocmanager/folders",
                {
                  name: newFolderName,
                  parent_id: null,
                }
              );
              setCreateOpen(false);
              setNewFolderName("");
              setLoading(true);
              const res = await axios.get(
                "https://server-9p4hx.kinsta.app/api/stocmanager/folder-groups"
              );
              setGroups(res.data);
              setLoading(false);
            }}
          >
            Salvează
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default MobileFolderGroups;
